/* eslint-disable react/prop-types */
/**
 * Admin → Appearance — choose the look every user of this app is drawn with.
 *
 * The presets (`classic`, `minimal`, `ledger`, `fluent`), the density switch
 * (`comfortable` / `compact`) and the cards come from
 * wig-react-library (`WigThemeSetting`); the choice is stored in de-hub's
 * shared App Support module (`/ui_settings`), so this screen is the same in
 * every app that adopts it. Ant Design stays underneath in every preset.
 *
 * `Classic` is the app as it looked before presets existed, and choosing it is
 * the off switch. A change reaches other users on their next page load.
 * "Preview for me" tries a look in this browser only, without touching anyone
 * else.
 *
 * Gated like Announcements: app-support admin roles (VITE_ADMIN_ROLE, BD.Admin
 * here, plus the de/admin superusers). Non-admins are redirected to `/`; the
 * de-hub PUT is the real boundary and 403s everyone else.
 */
import React, { useContext, useEffect } from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import { Alert, Button, Row, Space, Typography, message } from "antd";
import { useNavigate } from "react-router";

import {
  WigAntLayout,
  WigThemeSetting,
} from "@warwick-investment-group/wig-react-library";

import SignIn from "../../signIn";
import { AppContext } from "../../../AppContextProvider";
import useIsAdmin from "../../../hooks/useIsAdmin";
import { apiErrorText } from "../../../utils/apiError";

const { Title, Text } = Typography;

const Appearance = ({ darkMode }) => {
  const { theme, uiSettings, baseTheme } = useContext(AppContext);
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdmin) navigate("/");
  }, [isAdmin, navigate]);

  if (!isAdmin) return null;

  // Both saves report the same way. Returns whether the save worked.
  const save = async (apply, what) => {
    try {
      await apply();
      message.success(`${what} updated. Other users see it on their next page load.`);
      return true;
    } catch (e) {
      message.error(
        apiErrorText(e, { fallback: `Could not change the ${what.toLowerCase()}.` })
      );
      return false;
    }
  };

  const handleApply = async (preset) => {
    // A preview of some other look would hide the change just made.
    if (await save(() => uiSettings.applyThemePreset(preset), "Look")) {
      uiSettings.setThemePreview(null);
    }
  };

  const handleDensityApply = (next) =>
    save(() => uiSettings.applyDensity(next), "Density");

  return (
    <React.Fragment>
      <AuthenticatedTemplate>
        <Row>
          <WigAntLayout
            darkMode={theme === "dark" || darkMode}
            layoutStyle={{
              height: "93vh",
              width: "99vw",
              margin: 0,
              padding: 0,
              overflow: "hidden",
            }}
            contentStyle={{
              minHeight: "70vh",
              overflow: "auto",
              padding: 12,
              margin: 0,
            }}
            showFooter={false}
            showHeader={false}
            siderProps={{ collapsible: true, collapsed: true, width: 0 }}
            showDefaultMenu={false}
            content={
              <Space orientation="vertical" size={12} style={{ width: "100%" }}>
                <Title level={3} style={{ margin: 0 }}>
                  Appearance
                </Title>
                <Alert
                  type="info"
                  showIcon
                  title="Try a new look, and turn it off in one click"
                  description={
                    <Text>
                      Preview a look in your own browser first. Apply it to
                      everyone when you are happy; choose Classic to go back.
                      Density set here is the default: anyone can choose their
                      own under Density in the user menu. Data grids keep their
                      current look for now.
                    </Text>
                  }
                />
                {uiSettings.loadError && (
                  <Alert
                    type="error"
                    showIcon
                    title="Couldn't load the current look"
                    description="What is shown below may not be what everyone else sees, and changes can't be saved until it loads."
                    action={
                      <Button size="small" onClick={uiSettings.reload}>
                        Retry
                      </Button>
                    }
                  />
                )}
                <WigThemeSetting
                  value={uiSettings.themePreset}
                  canEdit={uiSettings.canEditTheme}
                  onApply={handleApply}
                  previewPreset={uiSettings.themePreview}
                  onPreviewChange={uiSettings.setThemePreview}
                  mode={theme === "dark" ? "dark" : "light"}
                  base={baseTheme}
                  density={uiSettings.density}
                  onDensityApply={handleDensityApply}
                />
              </Space>
            }
          />
        </Row>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
};

export default Appearance;
